import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { MaintenanceHubConnectionService } from "@app/core/services/hubs/maintenance-hub-connection.service";
import { Maintenance } from "@app/core/services/hubs/models/maintenance.model";
import { AlertValidationResult, TagAlert } from "@app/core/services/hubs/models/tag-alert.model";
import { PostOfficeService } from "@app/core/services/post-office/post-office.service";
import { SettingsService } from "@app/core/services/settings/settings.service";
import { AlertType, SecurityAlert } from "@app/shared/toasts/security-alert/models/security-alert";
import { SecurityAlertComponent } from "@app/shared/toasts/security-alert/security-alert.component";
import { HttpDebugToastComponent } from "@app/shared/toasts/http-debug/http-debug-toast.component";
import { CopyableErrorToastComponent, CopyableErrorToastData } from "@app/shared/toasts/copyable-error/copyable-error-toast.component";
import { MediaPlaybackService } from "@app/shared/services/media-playback.service";
import { TranslateService } from "@ngx-translate/core";
import { cloneDeep } from "lodash-es";
import { GlobalConfig, ToastrService } from "ngx-toastr";
import { BehaviorSubject, Observable } from "rxjs";
import { isAccessRuleTagAlert } from "./tag-alert-classification";

// Alert Options Interface
export interface GeneralAlertOptions {
    title: string;
    content: string;
    alertType: AlertType;
    photoUrl?: string;
    showImage?: boolean;
    detailsUrl?: string;
    detailsUrlParams?: any;
    locateUrl?: string;
    locateUrlParams?: any;
    disableTimeOut?: boolean;
    timeOut?: number;
    tapToDismiss?: boolean;
    closeButton?: boolean;
    preventDuplicates?: boolean;
}

export interface HttpDebugToastOptions {
    method: string;
    url: string;
    durationMs: number;
    rawBytes?: number | null;
    wireBytes?: number | null;
    waitMs?: number | null;
}

export interface CopyableErrorToastOptions extends CopyableErrorToastData {
    disableTimeOut?: boolean;
    timeOut?: number;
}

// Toast Timeout Configuration - Tüm süreleri buradan kolayca ayarlayabilirsin
export const TOAST_TIMEOUTS = {
    // Hızlı toast metodları için
    SUCCESS: 8000,          // 8 saniye
    ERROR: 30000,           // 30 saniye
    INFO: 12000,            // 12 saniye
    WARNING: 20000,         // 20 saniye

    // Genel default'lar
    DEFAULT_NORMAL: 15000,  // 15 saniye (normal alerts için)
    DEFAULT_ERROR: 30000,   // 30 saniye (error alerts için)

    // Özel durumlar
    SECURITY_ALERT: 20000,  // 20 saniye (güvenlik alertleri)
    MAINTENANCE: 15000,     // 15 saniye (maintenance completed)

} as const;

@Injectable({
    providedIn: 'root'
})
export class ToastNotifyService {    // Bildirim kuyruğu ve sessize alma ayarları
    private alertQueue: TagAlert[] = [];
    private generalAlertQueue: GeneralAlertOptions[] = []; // Genel alertler için kuyruk
    private muteDuration = 3600; // Sessize alma süresi saniye olarak (1 saat)
    private countdown$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private countdownInterval: any;
    private _isMuted = false;
    private mutedState$ = new BehaviorSubject<boolean>(false);
    private activeToastMap: Map<string, any> = new Map(); // Toast hash -> toast reference mapping
    // Mute sırasında gizlenen toast'ları saklamak için
    private hiddenToastsBeforeMute: Array<{
        messageHash: string;
        alert: SecurityAlert;
        count: number;
        timeRemaining: number; // Kalan süre
    }> = [];

    // Kuyruktaki mesaj sayısını takip et
    private queuedMessageCount$ = new BehaviorSubject<number>(0);
    private activeNotifications$ = new BehaviorSubject<number>(0);

    // Sessize alma durumunu getter ve setter ile kontrol et
    get isMuted(): boolean {
        return this._isMuted;
    }

    set isMuted(value: boolean) {
        this._isMuted = value;
        this.mutedState$.next(value);
        this.toggleMuteCountdown(value);
    }

    constructor(public toastr: ToastrService,
        private translateService: TranslateService,
        private maintenanceHub: MaintenanceHubConnectionService,
        private postOfficeService: PostOfficeService,
        private injector: Injector,
        private mediaPlayback: MediaPlaybackService,
        private router: Router) {
        this.syncActiveCount();
    }

    public setToastContainer(container: any) {
        // ToastrService içinde özel konteyneri tanımlayın
        this.toastr.overlayContainer = container;
    }

    /**
     * Genel Alert gösterme fonksiyonu - SecurityAlertComponent kullanır
     * Tüm alert tipleri için: success, error, info, warning
     * Duplicate mesajları sayaçla gösterir
     */
    showGeneralAlert(options: GeneralAlertOptions): any {
        // Eğer mute edilmişse, kuyruğa normal şekilde ekle (duplicate kontrolü yok)
        if (this.isMuted) {
            this.generalAlertQueue.push(options);
            this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
            return null;
        }

        // Duplicate mesaj kontrolü için hash oluştur
        const messageHash = this.createMessageHash(options.title, options.content, options.alertType);

        // Eğer aynı mesaj zaten varsa, mevcut toast'ı kapat ve yeni count ile tekrar aç
        if (this.activeToastMap.has(messageHash)) {
            const existingToastData = this.activeToastMap.get(messageHash);
            const newCount = existingToastData.count + 1;

            // Eski toast'ı kapat
            this.toastr.remove(existingToastData.toastRef.toastId);
            this.activeToastMap.delete(messageHash);

            // Yeni toast'ı arttırılmış count ile oluştur
            return this.createNewToast(options, messageHash, newCount);
        }

        // İlk kez gösterilen mesaj
        return this.createNewToast(options, messageHash, 1);
    }

    /**
     * Yeni toast oluşturur
     */
    private createNewToast(options: GeneralAlertOptions, messageHash: string, count: number): any {
        let toastrOptions: GlobalConfig;
        toastrOptions = this.toastr.toastrConfig;
        const opt = cloneDeep(toastrOptions);

        // SecurityAlertComponent kullan
        opt.toastComponent = SecurityAlertComponent;
        opt.toastClass = 'toast';
        opt.enableHtml = false;
        opt.disableTimeOut = options.disableTimeOut ?? false;
        opt.timeOut = options.timeOut ?? (options.alertType === AlertType.Error ? TOAST_TIMEOUTS.DEFAULT_ERROR : TOAST_TIMEOUTS.DEFAULT_NORMAL);
        opt.tapToDismiss = options.tapToDismiss ?? true;
        opt.countDuplicates = false;
        opt.preventDuplicates = false; // Bizim logic ile handle ediyoruz
        opt.closeButton = options.closeButton ?? true;
        opt.positionClass = 'inline';

        // Hover sırasında geri sayımın durması ve kaldığı yerden devam etmesi için
        // default extendedTimeOut değerini kullan

        // SecurityAlert objesi oluştur
        const securityAlert = new SecurityAlert();
        securityAlert.title = options.title;
        securityAlert.content = options.content;
        securityAlert.alertType = options.alertType;
        securityAlert.date = new Date();
        securityAlert.photoUrl = options.photoUrl;
        securityAlert.showImage = options.showImage ?? false;
        securityAlert.detailsUrl = options.detailsUrl;
        securityAlert.detailsUrlParams = options.detailsUrlParams;
        securityAlert.locateUrl = options.locateUrl;
        securityAlert.locateUrlParams = options.locateUrlParams;
        securityAlert.count = count; // Arttırılan count

        this.playNotificationSound(options.alertType);

        // Toast göster
        const toastRef = this.toastr.show<SecurityAlertComponent>(JSON.stringify(securityAlert), '', opt);
        this.syncActiveCount();

        // Toast'ı Map'e kaydet
        this.activeToastMap.set(messageHash, {
            toastRef,
            alert: securityAlert,
            count: count
        });

        // Toast dismiss edildiğinde Map'ten kaldır
        toastRef.onHidden.subscribe(() => {
            this.activeToastMap.delete(messageHash);
            this.syncActiveCount();
        });

        return toastRef;
    }

    /**
     * Hızlı Success toast
     */
    showSuccess(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Success,
            timeOut: TOAST_TIMEOUTS.SUCCESS,
            ...options
        });
    }

    showHttpDebugToast(options: HttpDebugToastOptions): any {
        let toastrOptions: GlobalConfig;
        toastrOptions = this.toastr.toastrConfig;
        const opt = cloneDeep(toastrOptions);

        opt.toastComponent = HttpDebugToastComponent;
        opt.toastClass = 'toast';
        opt.enableHtml = false;
        opt.disableTimeOut = false;
        opt.timeOut = 5000;
        opt.tapToDismiss = true;
        opt.closeButton = false;
        opt.positionClass = 'inline';
        opt.countDuplicates = false;
        opt.preventDuplicates = false;

        const toastRef = this.toastr.show(JSON.stringify(options), '', opt);
        this.syncActiveCount();
        toastRef.onHidden.subscribe(() => {
            this.syncActiveCount();
        });

        return toastRef;
    }

    showCopyableError(options: CopyableErrorToastOptions): any {
        let toastrOptions: GlobalConfig;
        toastrOptions = this.toastr.toastrConfig;
        const opt = cloneDeep(toastrOptions);

        opt.toastComponent = CopyableErrorToastComponent;
        opt.toastClass = 'toast';
        opt.enableHtml = false;
        opt.disableTimeOut = options.disableTimeOut ?? true;
        opt.timeOut = options.timeOut ?? TOAST_TIMEOUTS.ERROR;
        opt.tapToDismiss = false;
        opt.closeButton = false;
        opt.positionClass = 'inline';
        opt.countDuplicates = false;
        opt.preventDuplicates = false;

        this.playNotificationSound(AlertType.Error);
        const toastRef = this.toastr.show(JSON.stringify(options), '', opt);
        this.syncActiveCount();
        toastRef.onHidden.subscribe(() => {
            this.syncActiveCount();
        });

        return toastRef;
    }

    /**
     * Hızlı Error toast
     */
    showError(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Error,
            timeOut: TOAST_TIMEOUTS.ERROR,
            disableTimeOut: false,
            ...options
        });
    }

    /**
     * Hızlı Info toast
     */
    showInfo(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Info,
            timeOut: TOAST_TIMEOUTS.INFO,
            ...options
        });
    }

    /**
     * Hızlı Warning toast
     */
    showWarning(title: string, content: string, options?: Partial<GeneralAlertOptions>) {
        return this.showGeneralAlert({
            title,
            content,
            alertType: AlertType.Warning,
            timeOut: TOAST_TIMEOUTS.WARNING,
            ...options
        });
    }

    clearNotifications(): void {
        const activeToasts = this.toastr.toasts.slice(); // Snapshot before removal mutates the Toastr list.
        for (const toast of activeToasts) {
            this.toastr.remove(toast.toastId);
        }
        this.activeToastMap.clear();
        this.alertQueue = [];
        this.generalAlertQueue = [];
        this.hiddenToastsBeforeMute = [];
        this.queuedMessageCount$.next(0);
        this.syncActiveCount();
    }

    toggleMute() {
        if (this._isMuted) {
            this.unmute();
            return;
        }

        this.muteForSeconds(3600);
    }

    muteForSeconds(durationSeconds: number): void {
        this.muteDuration = Math.max(1, Math.floor(durationSeconds || 3600));

        if (this._isMuted) {
            clearInterval(this.countdownInterval);
            this.countdown$.next(this.muteDuration);
            this.startMuteCountdownInterval();
            this.mutedState$.next(true);
            return;
        }

        this.isMuted = true;
    }

    syncWebMuteState(isMuted: boolean, remainingSeconds: number): void {
        const normalizedRemainingSeconds = Math.max(0, Math.ceil(remainingSeconds || 0));
        if (isMuted && normalizedRemainingSeconds > 0) {
            this.muteForSeconds(normalizedRemainingSeconds);
            return;
        }

        this.unmute();
    }

    unmute(): void {
        if (!this._isMuted) {
            return;
        }

        this.isMuted = false;
    }

    public get activeNotifications(): number {
        return this.toastr.currentlyActive;
    }

    /**
     * Kuyruktaki mesaj sayısını döndürür
     */
    public get queuedMessageCount(): number {
        return this.alertQueue.length + this.generalAlertQueue.length;
    }

    /**
     * Kuyruktaki mesaj sayısını observable olarak döndürür
     */
    public getQueuedMessageCount(): Observable<number> {
        return this.queuedMessageCount$.asObservable();
    }

    public getActiveNotifications(): Observable<number> {
        return this.activeNotifications$.asObservable();
    }

    public getMuteState(): Observable<boolean> {
        return this.mutedState$.asObservable();
    }

    showRedirectAlert(url: string, message: string, countdownSeconds = 10): void {
        let remainingSeconds = Math.max(1, Math.floor(countdownSeconds || 10));
        let progress = 100;

        const opt = cloneDeep(this.toastr.toastrConfig);
        opt.toastComponent = SecurityAlertComponent;
        opt.toastClass = 'toast';
        opt.enableHtml = false;
        opt.disableTimeOut = true;
        opt.tapToDismiss = false;
        opt.countDuplicates = false;
        opt.preventDuplicates = true;
        opt.closeButton = true;
        opt.positionClass = 'inline';

        const securityAlert = new SecurityAlert();
        securityAlert.title = 'Yönlendirme Bildirimi';
        securityAlert.content = `${this.escapeHtml(message)}<br>${remainingSeconds} saniye sonra yönlendirileceksiniz.`;
        securityAlert.alertType = AlertType.Info;
        securityAlert.date = new Date();

        const toastRef = this.toastr.show<SecurityAlertComponent>(JSON.stringify(securityAlert), '', opt);
        this.syncActiveCount();

        if (toastRef?.toastRef.componentInstance) {
            toastRef.toastRef.componentInstance.progress = progress;
        }

        const countdownInterval = setInterval(() => {
            remainingSeconds -= 1;
            progress = Math.max(0, (remainingSeconds / Math.max(1, countdownSeconds || 10)) * 100);

            if (toastRef?.toastRef.componentInstance) {
                securityAlert.content = `${this.escapeHtml(message)}<br>${remainingSeconds} saniye sonra yönlendirileceksiniz.`;
                toastRef.toastRef.componentInstance.alert = securityAlert;
                toastRef.toastRef.componentInstance.progress = progress;
            }

            if (remainingSeconds <= 0) {
                clearInterval(countdownInterval);
                this.toastr.clear(toastRef.toastId);
                this.navigateFromRedirectAlert(url);
            }
        }, 1000);

        toastRef.onHidden.subscribe(() => {
            clearInterval(countdownInterval);
            this.syncActiveCount();
        });
    }


    showMaintenanceAlert(data: Maintenance.FragmentationProgress) {
        // Yeni genel alert sistemini kullan
        const toastRef = this.showGeneralAlert({
            title: 'Database Optimization in Progress',
            content: `
                <p class="text-gray-700">
                    You may experience <span class="underline decoration-2 decoration-sky-500 font-bold">temporary slowdowns</span> until the process is complete.
                </p>
            `,
            alertType: AlertType.Info,
            detailsUrl: '/maintenances/database',
            disableTimeOut: true,
            tapToDismiss: false
        });

        if (!toastRef) {
            return;
        }

        // Pass the currentProgress dynamically to the toast component
        if (toastRef.toastRef.componentInstance) {
            this.maintenanceHub.fragmentationProgress$.subscribe((md) => {
                toastRef.toastRef.componentInstance.progress = md.progress;
                if (md.progress == 100) {
                    const alertInstance = toastRef.toastRef.componentInstance.alert;

                    alertInstance.alertType = AlertType.Success;
                    alertInstance.title = 'Database Optimization Completed';
                    alertInstance.content = `
                        <div class="text-gray-700">
                            <span>Your database has been <span class="underline decoration-2 decoration-green-500 font-bold">successfully optimized</span>. Please note that the system may experience temporary slowdowns as the changes take effect. This is expected and should resolve shortly as the system stabilizes.</span>
                        </div>`;

                    //püf noktası
                    toastRef.toastRef.componentInstance.alert = alertInstance;
                    //dismiss dialog after 10 seconds
                    setTimeout(() => {
                        this.toastr.clear(toastRef.toastId);
                    }, TOAST_TIMEOUTS.MAINTENANCE);
                }
            });
        }
    }


    // Geri sayımı başlatır veya durdurur
    private toggleMuteCountdown(isMuted: boolean) {
        clearInterval(this.countdownInterval);

        if (isMuted) {
            // Mevcut toast'ları gizle ve sakla (silme!)
            this.hideCurrentToasts();

            // Countdown başlat
            this.countdown$.next(this.muteDuration);
            this.startMuteCountdownInterval();
        } else {
            // Manuel unmute
            this.clearMuteCountdown();
            this.restoreHiddenToasts(); // Gizlenen toast'ları geri yükle
            this.displayQueuedAlerts(); // Queue'daki yeni alertleri göster
        }
    }

    private startMuteCountdownInterval(): void {
        this.countdownInterval = setInterval(() => {
            const current = this.countdown$.value;
            if (current > 0) {
                this.countdown$.next(current - 1);
                return;
            }

            // Süre doldu, otomatik unmute
            this.clearMuteCountdown();
            this.restoreHiddenToasts(); // Gizlenen toast'ları geri yükle
            this.displayQueuedAlerts(); // Queue'daki yeni alertleri göster
        }, 1000);
    }

    // Geri sayım intervalini temizler
    private clearMuteCountdown() {
        clearInterval(this.countdownInterval);
        this._isMuted = false;
        this.mutedState$.next(false);
        this.countdown$.next(0);
        // Queue temizlendiğinde sayacı da güncelle
        this.queuedMessageCount$.next(this.queuedMessageCount);
    }

    // Kuyrukta biriken bildirimleri sırayla gösterir
    private displayQueuedAlerts() {
        setTimeout(async () => {
            // Önce TagAlert kuyruğunu işle
            while (this.alertQueue.length > 0) {
                const alert = this.alertQueue.shift();
                this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
                if (alert) {
                    this.showAlert(alert);
                    await new Promise(resolve => setTimeout(resolve, 250));
                }
            }

            // Sonra GeneralAlert kuyruğunu işle
            while (this.generalAlertQueue.length > 0) {
                const generalAlert = this.generalAlertQueue.shift();
                this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
                if (generalAlert) {
                    // Queue'dan çıkan mesajı normal pipeline'a gönder
                    // Bu sayede mevcut duplicate kontrolü çalışır
                    // Eğer timeout tanımsızsa varsayılan değerleri ayarla
                    if (generalAlert.disableTimeOut === undefined) {
                        generalAlert.disableTimeOut = false;
                    }

                    if (!generalAlert.timeOut || generalAlert.timeOut <= 0) {
                        generalAlert.timeOut = TOAST_TIMEOUTS.DEFAULT_NORMAL;
                    }

                    this.showGeneralAlert(generalAlert);
                    await new Promise(resolve => setTimeout(resolve, 250));
                }
            }
        }, 250);
    }

    // Sessize alma süresi boyunca bildirimleri kuyrukta biriktirir
    async showAlert(tagAlert: TagAlert) {
        if (this.isMuted) {
            this.alertQueue.push(tagAlert);
            this.queuedMessageCount$.next(this.queuedMessageCount); // Sayacı güncelle
            return;
        }

        // Normal gösterim kodları burada devam ediyor...
        var tag = await this.postOfficeService.getTag(tagAlert.tagId, undefined);

        if (tag.trackItemId == null) return;

        var trackItem = await this.postOfficeService.getTrackItem(tag.trackItemId);
        var infantTagTypeId = this.injector.get(SettingsService).getSettingValue('Infant.Baby.Tag.Type');
        let isInfantTag = tag.tagTypeId == infantTagTypeId;
        const isAccessRuleAlert = isAccessRuleTagAlert(tagAlert);

        const title = isAccessRuleAlert
            ? this.translateService.instant(
                tagAlert.alert == AlertValidationResult.Alert
                    ? 'SystemAdmin.Alerts.SecurityAlert.AccessRule.Title'
                    : 'SystemAdmin.Alerts.SecurityAlert.AccessRule.Resolved.Title'
            )
            : this.translateService.instant(
                tagAlert.alert == AlertValidationResult.Alert
                    ? 'SystemAdmin.Alerts.SecurityAlert.Title'
                    : 'SystemAdmin.Alerts.SecurityAlert.Resolved.Title'
            );

        const alertType = tagAlert.alert == AlertValidationResult.Alert ? AlertType.Error : AlertType.Success;

        // Build value info using comparison type and threshold values
        let valueInfo = '';

        try {
            if (tagAlert.measuredValue && tagAlert.thresholdValue && tagAlert.comparisonType) {
                const tol = tagAlert.tolerance > 0 ? ` (±${tagAlert.tolerance})` : '';

                switch (tagAlert.comparisonType) {
                    case 'GreaterThan':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.GreaterThan', {
                            measured: tagAlert.measuredValue,
                            threshold: tagAlert.thresholdValue + tol
                        });
                        break;
                    case 'LessThan':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.LessThan', {
                            measured: tagAlert.measuredValue,
                            threshold: tagAlert.thresholdValue + tol
                        });
                        break;
                    case 'RangeIn':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.RangeIn', {
                            measured: tagAlert.measuredValue,
                            thresholdA: tagAlert.thresholdValue,
                            thresholdB: tagAlert.thresholdValueB,
                            tolerance: tol
                        });
                        break;
                    case 'RangeOut':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.RangeOut', {
                            measured: tagAlert.measuredValue,
                            thresholdA: tagAlert.thresholdValue,
                            thresholdB: tagAlert.thresholdValueB,
                            tolerance: tol
                        });
                        break;
                    case 'RangeOutGreater':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.RangeOutGreater', {
                            measured: tagAlert.measuredValue,
                            upper: tagAlert.thresholdValueB || tagAlert.thresholdValue,
                            tolerance: tol
                        });
                        break;
                    case 'RangeOutLess':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.RangeOutLess', {
                            measured: tagAlert.measuredValue,
                            lower: tagAlert.thresholdValue || tagAlert.thresholdValueB,
                            tolerance: tol
                        });
                        break;
                    case 'Contain':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.Contain', {
                            measured: tagAlert.measuredValue,
                            threshold: tagAlert.thresholdValue
                        });
                        break;
                    case 'Exact':
                        valueInfo = this.translateService.instant('SystemAdmin.Alerts.ValueInfo.Exact', {
                            measured: tagAlert.measuredValue,
                            threshold: tagAlert.thresholdValue + tol
                        });
                        break;
                    default:
                        break;
                }
            }
        } catch (error) {
            valueInfo = '';
        }

        const message = isAccessRuleAlert
            ? this.translateService.instant(
                tagAlert.alert == AlertValidationResult.Alert
                    ? 'SystemAdmin.Alerts.SecurityAlert.AccessRule.Description'
                    : 'SystemAdmin.Alerts.SecurityAlert.AccessRule.Resolved.Description',
                {
                    zoneName: this.getAccessRuleZoneLabel(tagAlert),
                    ruleName: this.getAccessRuleLabel(tagAlert),
                    trackItemDescription: trackItem.description,
                    tagName: tag.name
                }
            )
            : this.translateService.instant(
                tagAlert.alert == AlertValidationResult.Alert
                    ? 'SystemAdmin.Alerts.SecurityAlert.SensorDescription'
                    : 'SystemAdmin.Alerts.SecurityAlert.Resolved.SensorDescription',
                {
                    sensorProperty: tagAlert.propertyName,
                    trackItemDescription: trackItem.description,
                    tagName: tag.name,
                    valueInfo: valueInfo
                }
            );

        const content = `<p>${message}</p>`;

        let detailsUrl, detailsUrlParams;
        if (isInfantTag) {
            detailsUrl = `/object-safeties`;
            detailsUrlParams = { 'child-tag': tagAlert.tagId };
        }

        // showGeneralAlert kullanarak duplicate detection'ı etkinleştir
        this.showGeneralAlert({
            title: title,
            content: content,
            alertType: alertType,
            photoUrl: trackItem.photoUrl,
            showImage: true,
            locateUrl: '/live',
            locateUrlParams: { locate: 'tag', id: tagAlert.tagSessionHistoryId },
            detailsUrl: detailsUrl,
            detailsUrlParams: detailsUrlParams,
            timeOut: TOAST_TIMEOUTS.SECURITY_ALERT,
            disableTimeOut: false,
            tapToDismiss: true,
            closeButton: true
        });
    }

    private getAccessRuleZoneLabel(tagAlert: TagAlert): string {
        return tagAlert.zoneName?.trim()
            || this.translateService.instant('SystemAdmin.Alerts.SecurityAlert.AccessRule.UnknownZone');
    }

    private getAccessRuleLabel(tagAlert: TagAlert): string {
        return tagAlert.accessRuleName?.trim()
            || this.translateService.instant('SystemAdmin.Alerts.SecurityAlert.AccessRule.DefaultPolicy');
    }

    // Geri sayımı takip eden observable (komponentte göstermek için)
    getCountdown(): Observable<number> {
        return this.countdown$.asObservable();
    }

    /**
     * Mesaj için unique hash oluşturur
     */
    private createMessageHash(title: string, content: string, alertType: AlertType): string {
        const hashInput = `${title}|${content}|${alertType}`;
        // Basit hash function (production'da daha güçlü bir hash kullanılabilir)
        let hash = 0;
        for (let i = 0; i < hashInput.length; i++) {
            const char = hashInput.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // 32-bit integer
        }
        return hash.toString();
    }

    private navigateFromRedirectAlert(url: string): void {
        const target = String(url || '').trim();
        if (target.startsWith('/') && !target.startsWith('//')) {
            void this.router.navigateByUrl(target);
            return;
        }

        try {
            const parsedUrl = new URL(target, window.location.origin);
            if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
                window.location.href = parsedUrl.toString();
                return;
            }
        } catch {
            // Invalid URLs are ignored after the user-visible countdown.
        }

        this.showWarning('Yönlendirme engellendi', 'Geçersiz veya desteklenmeyen bir adres istendi.');
    }

    private escapeHtml(value: string): string {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    /**
     * Mevcut toast'ları gizler ve bilgilerini saklar
     */
    private hideCurrentToasts() {
        this.hiddenToastsBeforeMute = [];

        // Mevcut aktif toast'ları kaydet
        this.activeToastMap.forEach((toastData, messageHash) => {
            const { toastRef, alert, count } = toastData;

            // Kalan süreyi hesapla (eğer timeout varsa)
            let timeRemaining = 0;
            if (toastRef.toastConfig && toastRef.toastConfig.timeOut && !toastRef.toastConfig.disableTimeOut) {
                // Toast'ın ne kadar süredir açık olduğunu hesapla
                const elapsedTime = Date.now() - alert.date.getTime();
                timeRemaining = Math.max(0, toastRef.toastConfig.timeOut - elapsedTime);
            }

            // Toast bilgilerini sakla
            this.hiddenToastsBeforeMute.push({
                messageHash,
                alert: { ...alert }, // Deep copy
                count,
                timeRemaining
            });

            // Toast'ı kapat (ama Map'ten hemen silme, onHidden'da silinecek)
            this.toastr.remove(toastRef.toastId);
        });

    }

    /**
     * Mute öncesi gizlenen toast'ları tekrar gösterir
     */
    private restoreHiddenToasts() {
        if (this.hiddenToastsBeforeMute.length === 0) {
            return;
        }

        // Gizlenen toast'ları sırayla tekrar göster
        setTimeout(async () => {
            for (const hiddenToast of this.hiddenToastsBeforeMute) {
                const { alert, count, timeRemaining } = hiddenToast;

                // Toast options'ını yeniden oluştur
                const options: GeneralAlertOptions = {
                    title: alert.title,
                    content: alert.content,
                    alertType: alert.alertType,
                    photoUrl: alert.photoUrl,
                    showImage: alert.showImage,
                    detailsUrl: alert.detailsUrl,
                    detailsUrlParams: alert.detailsUrlParams,
                    locateUrl: alert.locateUrl,
                    locateUrlParams: alert.locateUrlParams,
                    // Süre dolmuş olsa bile geri geldiğinde mutlaka geri sayım başlasın
                    timeOut: timeRemaining > 1000 ? timeRemaining : TOAST_TIMEOUTS.DEFAULT_NORMAL,
                    disableTimeOut: false,
                    tapToDismiss: true,
                    closeButton: true
                };

                // Hash'i yeniden oluştur
                const messageHash = this.createMessageHash(alert.title, alert.content, alert.alertType);

                // Toast'ı tekrar göster
                this.createNewToast(options, messageHash, count);

                // Toastlar arasında kısa bekleme
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Hidden list'i temizle
            this.hiddenToastsBeforeMute = [];
        }, 200);
    }

    private playNotificationSound(alertType: AlertType): void {
        if (this.isMuted) {
            return;
        }

        switch (alertType) {
            case AlertType.Error:
                this.mediaPlayback.playNotificationSound('error');
                break;
            case AlertType.Success:
                this.mediaPlayback.playNotificationSound('success');
                break;
            case AlertType.Warning:
                this.mediaPlayback.playNotificationSound('warning');
                break;
            default:
                this.mediaPlayback.playNotificationSound('info');
                break;
        }
    }

    private syncActiveCount(): void {
        this.activeNotifications$.next(this.toastr.currentlyActive);
    }
}
